"use client"
import { useState, useEffect } from "react"
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { db } from "../config/firebase"
import { useAuth } from "../AuthContext"

const DISPLAY_FONT = "Satoshi, Inter, SF Pro Display, system-ui, sans-serif"
const BODY_FONT    = "Inter, SF Pro Text, system-ui, sans-serif"

export default function PageHeader({
  docId,
  defaultTitle,
  defaultDescription,
  sx = {},       // wrapper Box overrides
  titleSx = {},  // h2 overrides
  descSx = {},   // h5 overrides
}) {
  const { isAdmin } = useAuth()
  const [content, setContent]     = useState({ title: defaultTitle, description: defaultDescription })
  const [editOpen, setEditOpen]   = useState(false)
  const [draft, setDraft]         = useState({ title: defaultTitle, description: defaultDescription })
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "siteContent", docId))
        if (snap.exists()) {
          setContent({
            title:       snap.data().title       || defaultTitle,
            description: snap.data().description || defaultDescription,
          })
        }
      } catch (err) {
        console.error(`Failed to load ${docId}:`, err)
      }
    }
    fetch()
  }, [docId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    setDraft({ ...content })
    setSaved(false)
    setSaveError(null)
    setEditOpen(true)
  }

  const handleClose = () => setEditOpen(false)

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      await setDoc(doc(db, "siteContent", docId), draft)
      setContent({ ...draft })
      setSaved(true)
      setTimeout(handleClose, 1200)
    } catch (err) {
      console.error(`Failed to save ${docId}:`, err)
      setSaveError(
        err.code === "permission-denied"
          ? "Permission denied. Make sure the Firestore rules are deployed."
          : err.message || "Save failed. Please try again."
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Box sx={{ mb: 5, textAlign: "center", ...sx }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "#414040ff",
              textShadow: "0 2px 18px #fff6",
              mb: 1,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              ...titleSx,
            }}
          >
            {content.title}
          </Typography>
          {isAdmin && (
            <IconButton
              onClick={handleOpen}
              size="small"
              title="Edit header"
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                width: 30,
                height: 30,
                flexShrink: 0,
                alignSelf: "flex-start",
                mt: "6px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" },
              }}
            >
              <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ width: 90, height: 4, background: "linear-gradient(90deg, #002147 0%, #005792 100%)", borderRadius: 2, margin: "18px auto", opacity: 0.6 }} />

        <Typography
          variant="h5"
          sx={{
            fontFamily: BODY_FONT,
            color: "#000",
            maxWidth: 640,
            mx: "auto",
            fontWeight: 400,
            mb: 2,
            lineHeight: 1.5,
            fontSize: { xs: "1.1rem", md: "1.3rem" },
            ...descSx,
          }}
        >
          {content.description}
        </Typography>
      </Box>

      <Dialog open={editOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: BODY_FONT, fontWeight: 600 }}>Edit Page Header</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Page Title"
            value={draft.title}
            onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))}
            fullWidth size="small"
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
            fullWidth size="small"
            multiline rows={4}
          />
        </DialogContent>
        {saveError && (
          <Box sx={{ px: 3, pt: 1, color: "#b91c1c", fontSize: "0.82rem" }}>{saveError}</Box>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || saved}
            variant="contained"
            sx={{
              backgroundColor: saved ? "#1a7a4a" : "#0d1b4c",
              "&:hover": { backgroundColor: saved ? "#1a7a4a" : "#1a2d5a" },
              "&.Mui-disabled": { backgroundColor: saved ? "#1a7a4a" : undefined, color: saved ? "#fff" : undefined },
              fontFamily: BODY_FONT,
            }}
          >
            {saved ? "Saved!" : saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
